import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { Pickaxe, ArrowLeft, Sparkles, ShieldCheck, TrendingUp, Mail } from "lucide-react";

const Card = ({ title, children, testid }) => (
  <div className="cad-panel p-4" data-testid={testid}>
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">{title}</div>
    <div className="mt-2">{children}</div>
  </div>
);

export const MiningInvestorPortal = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["mining-investor-portal"],
    queryFn: async () => (await api.get("/cadastre360/mineral/investor-portal")).data,
    retry: false,
  });

  return (
    <div className="min-h-screen p-6 lg:p-12" data-testid="page-mining-investor-portal">
      <div className="flex items-center justify-between mb-8">
        <BrandMark size={140} />
        <Link to="/portal" className="cad-btn-outline px-3 py-1.5 rounded-sm text-[12px] inline-flex items-center gap-1.5">
          <ArrowLeft size={12} /> Public Portal
        </Link>
      </div>

      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-start gap-4">
          <Pickaxe size={40} className="text-[#C99A2E]" />
          <div>
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#C99A2E]">
              Republic of Zimbabwe · Cadastre360 Mining
            </div>
            <h1 className="font-display text-4xl font-bold text-[#F6F8FA] mt-1">Sovereign Mining Investment Portal</h1>
            <div className="text-[13px] text-[#B3C2CC] mt-2 max-w-3xl">
              {data?.sovereign_message}
            </div>
          </div>
        </div>

        {isLoading && <div className="cad-panel p-8 text-[#B3C2CC]">Loading investor portal…</div>}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card title="Strategic minerals" testid="portal-strategic">
                <div className="text-[13px] text-[#F6F8FA] font-mono-cad">{(data.strategic_minerals || []).join(" · ")}</div>
              </Card>
              <Card title="Open ground" testid="portal-open-ground">
                <div className="text-[26px] font-display font-bold text-[#F6F8FA]">{(data.open_ground_km2 || 0).toLocaleString()} km²</div>
              </Card>
              <Card title="Blocks available" testid="portal-blocks-count">
                <div className="text-[26px] font-display font-bold text-[#F6F8FA]">{(data.available_blocks || []).length}</div>
              </Card>
              <Card title="Contact agency" testid="portal-contact">
                <div className="text-[12px] text-[#F6F8FA]">{data.contact?.agency}</div>
                <a href={"mailto:" + data.contact?.email} className="text-[11px] font-mono-cad text-[#C99A2E] inline-flex items-center gap-1 mt-1">
                  <Mail size={11} /> {data.contact?.email}
                </a>
              </Card>
            </div>

            <Card title="Investment incentives" testid="portal-incentives">
              <ul className="list-disc list-inside space-y-1 text-[13px] text-[#F6F8FA]">
                {(data.investment_incentives || []).map((i, ix) => <li key={ix}>{i}</li>)}
              </ul>
            </Card>

            <Card title="Available mineral blocks" testid="portal-blocks">
              <div className="overflow-x-auto">
                <table className="w-full text-[12px] min-w-[720px]">
                  <thead>
                    <tr className="text-left border-b cad-hairline">
                      {["Commodity", "Province", "Area (km²)", "Resource estimate", "Grade", "NPV (USD m)", "Readiness"].map((h) => (
                        <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="cad-tbody">
                    {(data.available_blocks || []).map((b) => (
                      <tr key={b.id}>
                        <td className="px-3 py-2 font-mono-cad text-[11px] text-[#C99A2E]">{b.commodity}</td>
                        <td className="px-3 py-2 text-[#F6F8FA]">{b.province}</td>
                        <td className="px-3 py-2 font-mono-cad">{b.area_km2.toLocaleString()}</td>
                        <td className="px-3 py-2 font-mono-cad text-[11px]">{b.resource_estimate}</td>
                        <td className="px-3 py-2 font-mono-cad">{b.grade}</td>
                        <td className="px-3 py-2 font-mono-cad text-[#6BB88A]">{b.npv_usd_m}</td>
                        <td className="px-3 py-2"><span className="cad-chip cad-chip-blue text-[9px] uppercase">{b.investment_readiness}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Card title="Geological due diligence" testid="portal-dd-geo">
                <div className="text-[12px] text-[#B3C2CC]">Regional geology · drillholes · assays · resource models</div>
                <a href={data.due_diligence_pack?.geological} target="_blank" rel="noreferrer"
                   className="cad-btn-outline mt-3 px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1">
                  <TrendingUp size={11} /> Access geological repository
                </a>
              </Card>
              <Card title="ESG & sustainability" testid="portal-dd-esg">
                <div className="text-[12px] text-[#B3C2CC]">Environmental · tailings GISTM · community · closure</div>
                <a href={data.due_diligence_pack?.esg} target="_blank" rel="noreferrer"
                   className="cad-btn-outline mt-3 px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1">
                  <ShieldCheck size={11} /> Access ESG disclosures
                </a>
              </Card>
              <Card title="EITI transparency dossier" testid="portal-dd-eiti">
                <div className="text-[12px] text-[#B3C2CC]">Sovereign EITI validation dossier — 88% ready</div>
                <a href={data.due_diligence_pack?.eiti_dossier} target="_blank" rel="noreferrer"
                   className="cad-btn-outline mt-3 px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1">
                  <Sparkles size={11} /> Access EITI dossier
                </a>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
